import { useEffect, useState } from "react";
import { useDeviceContext } from "../context/DeviceContext";
import { useFeatureFlagContext } from "./useFeatureFlagContext";
import { applyPalette, clearPalette, getStoredPalette } from "../utils/palette";

/**
 * Keeps the page's colour palette in step with this device's choice: reads it
 * once at start, then re-applies whenever it or the feature flag changes.
 * Classic only shows while `alphaClassicPalette` is on.
 */
export const usePaletteSync = (): void => {
  const { palette, loadPalette } = useDeviceContext();
  const { isFlagEnabled, featureFlagsLoaded } = useFeatureFlagContext();
  const [paletteLoaded, setPaletteLoaded] = useState(false);
  const featureOn = isFlagEnabled("alphaClassicPalette");
  // Until both are read, leave whatever the pre-paint script already set alone.
  const ready = featureFlagsLoaded && paletteLoaded;

  useEffect(() => {
    let live = true;
    getStoredPalette().then((stored) => {
      if (live) {
        loadPalette(stored);
        setPaletteLoaded(true);
      }
    });
    return () => {
      live = false;
    };
  }, [loadPalette]);

  useEffect(() => {
    if (!ready) return;
    // Classic only applies while the feature is on.
    if (featureOn) applyPalette(palette);
    else clearPalette();
  }, [palette, featureOn, ready]);
};
